// Typography rules (see src/design-system/TYPOGRAPHY_AND_THEME.md):
//   - Every title uses a typography.h* preset, OR `font-heading font-bold` directly.
//   - Body text inherits --font-body automatically; do not set fontFamily.
//   - Brand wordmark (myvitae.ai) keeps Quicksand via logoFontStyle — don't touch.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  CopyIcon,
  EmptyState,
  GhostButton,
  PencilIcon,
  PlusIcon,
  PrimaryButton,
  SearchInput,
  SecondaryButton,
  SettingsDotsIcon,
  TrashIcon,
} from "../../design-system";
import { ClockIcon, PlayIcon } from "../../design-system/icons";
import { borders, interactive, surfaces, textColors, typography } from "../../design-system/tokens";
import AutomationEditor from "../components/automations/AutomationEditor";
import AutomationRunHistorySection from "../components/automations/AutomationRunHistorySection";
import AutomationRunsDrawer from "../components/automations/AutomationRunsDrawer";
import AutomationSettingsCard from "../components/automations/AutomationSettingsCard";
import AutomationStateBadge from "../components/automations/AutomationStateBadge";
import RunFunnel, { runToFunnelCounts } from "../components/automations/RunFunnel";
import {
  AUTOMATION_STATES,
  automationToPreviewPayload,
  findCondition,
  formatCount,
  formatDelay,
  formatEmailVariant,
  formatParamsSummary,
  formatRelativeTime,
  formatRunDays,
  formatTrigger,
  getStateMeta,
  summarizeAutomations,
  summarizeChannels,
  uniqueEmailVariants,
} from "../components/automations/automationUtils";
import { useAdminAutomationConditions } from "../hooks/useAdminAutomationConditions";
import {
  useAdminAutomations,
  useDeleteAdminAutomation,
  useRunAdminAutomationNow,
  useSetAdminAutomationState,
} from "../hooks/useAdminAutomations";
import { adminAutomationPreview } from "../utils/adminApi";
import { Toast, useToast } from "../utils/adminToast";
import { messagesPathFor } from "../components/messages/messagesUtils";

// ---------------------------------------------------------------------------
// Small presentational bits
// ---------------------------------------------------------------------------

const chipClass = `inline-flex items-center px-2 py-0.5 rounded-full border ${borders.glass} ${surfaces.glassCard} ${typography.caption} ${textColors.secondary} whitespace-nowrap`;

const countBadgeClass = (tone) =>
  tone === "danger"
    ? "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
    : tone === "warning"
      ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
      : `${surfaces.tertiary} ${textColors.secondary}`;

const SummaryTile = ({ label, value, hint, tone = "neutral" }) => (
  <div className={`rounded-2xl border ${borders.glass} ${surfaces.glass} px-4 py-3 min-w-0`} title={hint}>
    <div className={`${typography.caption} ${textColors.tertiary} uppercase tracking-wider truncate`}>{label}</div>
    <div
      className={`mt-1 font-heading text-2xl font-bold tabular-nums ${
        tone === "success"
          ? "text-green-700 dark:text-green-300"
          : tone === "warning"
            ? "text-yellow-700 dark:text-yellow-300"
            : textColors.primary
      }`}
    >
      {formatCount(value)}
    </div>
  </div>
);

SummaryTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  hint: PropTypes.string,
  tone: PropTypes.oneOf(["neutral", "success", "warning"]),
};

const ChannelChips = ({ actions }) => {
  const channels = summarizeChannels(actions);
  const variants = uniqueEmailVariants(actions);
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {channels.inbox ? <span className={chipClass}>Inbox</span> : null}
      {channels.email ? (
        <span className={chipClass} title={variants.length ? `Email templates: ${variants.map(formatEmailVariant).join(", ")}` : ""}>
          Email{variants.length ? ` · ${variants.map(formatEmailVariant).join(", ")}` : ""}
        </span>
      ) : null}
      {!channels.inbox && !channels.email ? <span className={`${typography.caption} ${textColors.muted}`}>No channels</span> : null}
    </span>
  );
};

ChannelChips.propTypes = { actions: PropTypes.array };

const LastRunCell = ({ automation }) => {
  const run = automation.last_run;
  if (!run) {
    return <span className={`${typography.caption} ${textColors.muted}`}>Never run</span>;
  }
  const failed = run.status === "failed";
  const suppressed = Number(run.policy_suppressed_count || 0);
  return (
    <div className="space-y-1 min-w-0">
      <div className={`${typography.bodySmall} ${textColors.primary}`} title={run.started_at || ""}>
        {formatRelativeTime(run.started_at)}
      </div>
      <RunFunnel counts={runToFunnelCounts(run)} mode="run" variant="compact" />
      <div className="flex flex-wrap gap-1">
        {run.dry_run ? <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${countBadgeClass("neutral")}`}>Dry run</span> : null}
        {failed ? (
          <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${countBadgeClass("danger")}`} title={run.error || "Run failed"}>
            Failed
          </span>
        ) : null}
        {suppressed > 0 ? (
          <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${countBadgeClass("warning")}`} title="Held back by the delivery policy">
            {formatCount(suppressed)} suppressed
          </span>
        ) : null}
      </div>
    </div>
  );
};

LastRunCell.propTypes = { automation: PropTypes.object.isRequired };

const MessagesCell = ({ counts, automation }) => {
  const c = counts || {};
  const failed = Number(c.failed || 0);
  const suppressed = Number(c.suppressed || 0);
  const content = (
    <div className={`${typography.bodySmall} ${textColors.primary} space-y-0.5 whitespace-nowrap tabular-nums`}>
      <div>
        <span className="font-medium">{formatCount(c.scheduled || 0)}</span>{" "}
        <span className={`${typography.caption} ${textColors.tertiary}`}>scheduled</span>
      </div>
      <div>
        <span className="font-medium">{formatCount(c.sent || 0)}</span>{" "}
        <span className={`${typography.caption} ${textColors.tertiary}`}>sent</span>
      </div>
      {failed > 0 || suppressed > 0 ? (
        <div className="flex flex-wrap gap-1 pt-0.5">
          {failed > 0 ? (
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${countBadgeClass("danger")}`}>{formatCount(failed)} failed</span>
          ) : null}
          {suppressed > 0 ? (
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${countBadgeClass("warning")}`}>{formatCount(suppressed)} suppressed</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
  if (automation?.id == null) return content;
  // Deep link into the Messages tab, pre-filtered to this automation.
  return (
    <Link
      to={messagesPathFor({ origin: "automation", automation_id: String(automation.id) })}
      className="group inline-block rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
      title="Open these messages in the Messages tab"
      aria-label={`Open the messages of “${automation.name}” in the Messages tab`}
    >
      {content}
      <span className={`block mt-1 ${typography.caption} ${interactive.link} ${interactive.linkHover} group-hover:underline`}>
        Open in Messages
      </span>
    </Link>
  );
};

MessagesCell.propTypes = { counts: PropTypes.object, automation: PropTypes.object };

// ---------------------------------------------------------------------------
// Row actions: Edit + Activate/Pause visible, the rest in a "⋯" menu
// ---------------------------------------------------------------------------

const MENU_WIDTH = 208; // w-52

const RowActionsMenu = ({ automation, onRunNow, onViewRuns, onDuplicate, onDelete, onSetDraft }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  // The table scrolls horizontally inside a rounded, overflow-hidden box, so the
  // menu is portalled to <body> and positioned from the trigger's rect.
  const updatePosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect?.();
    if (!rect) return;
    const left = Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
    setPosition({ top: rect.bottom + 4, left });
  }, []);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (buttonRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus?.();
      }
    };
    const onReposition = () => updatePosition();
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    const timer = setTimeout(() => menuRef.current?.querySelector?.("[role='menuitem']")?.focus?.(), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, updatePosition]);

  const items = [
    // Event automations fire when their event happens — there is nothing to run on demand.
    ...(automation.trigger_type === "event"
      ? []
      : [{ key: "run", label: automation.state === "draft" ? "Run now (dry run)" : "Run now", icon: <PlayIcon size="sm" />, onClick: onRunNow }]),
    { key: "runs", label: "View runs", icon: <ClockIcon size="sm" />, onClick: onViewRuns },
    { key: "duplicate", label: "Duplicate", icon: <CopyIcon size="sm" />, onClick: onDuplicate },
    ...(automation.state !== "draft"
      ? [{ key: "draft", label: "Move back to draft", icon: <PencilIcon size="sm" />, onClick: onSetDraft }]
      : []),
    { key: "delete", label: "Delete", icon: <TrashIcon size="sm" />, onClick: onDelete, danger: true },
  ];

  const menu = open ? (
    <div
      ref={menuRef}
      role="menu"
      aria-label={`Actions for ${automation.name}`}
      style={{ position: "fixed", top: position.top, left: position.left, width: MENU_WIDTH }}
      className={`${surfaces.elevated} rounded-xl shadow-lg ${borders.default} border py-1 z-[4000]`}
    >
      {items.map((item, index) => (
        <React.Fragment key={item.key}>
          {item.danger && index > 0 ? <div className={`border-t ${borders.default} my-1`} /> : null}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              item.onClick();
            }}
            className={`w-full text-left px-3 py-2 inline-flex items-center gap-2 ${typography.bodySmall} ${
              item.danger ? "text-red-600 dark:text-red-400" : textColors.primary
            } ${surfaces.hover} transition-colors focus:outline-none focus:bg-gray-50 dark:focus:bg-dark-800`}
          >
            <span className="shrink-0" aria-hidden="true">
              {item.icon}
            </span>
            {item.label}
          </button>
        </React.Fragment>
      ))}
    </div>
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ${textColors.secondary} ${surfaces.hover} focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400`}
        aria-label={`More actions for ${automation.name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title="More actions"
      >
        <SettingsDotsIcon size="sm" />
      </button>
      {menu && typeof document !== "undefined" ? createPortal(menu, document.body) : null}
    </>
  );
};

RowActionsMenu.propTypes = {
  automation: PropTypes.object.isRequired,
  onRunNow: PropTypes.func.isRequired,
  onViewRuns: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSetDraft: PropTypes.func.isRequired,
};

const StateToggleButton = ({ automation, onActivate, onPause, busy }) => {
  if (automation.state === "active") {
    return (
      <SecondaryButton size="sm" onClick={onPause} disabled={busy} title="Pause — the scheduler skips this automation">
        Pause
      </SecondaryButton>
    );
  }
  return (
    <PrimaryButton size="sm" onClick={onActivate} disabled={busy} title={automation.state === "paused" ? "Resume on schedule" : "Activate — start running on schedule"}>
      {automation.state === "paused" ? "Resume" : "Activate"}
    </PrimaryButton>
  );
};

StateToggleButton.propTypes = {
  automation: PropTypes.object.isRequired,
  onActivate: PropTypes.func.isRequired,
  onPause: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const STATE_FILTERS = [{ value: "", label: "All" }, ...AUTOMATION_STATES.map((s) => ({ value: s, label: getStateMeta(s).label }))];

const SkeletonRows = () => (
  <div className="space-y-3" aria-busy="true" aria-live="polite">
    {[0, 1, 2].map((i) => (
      <div key={i} className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-4`}>
        <div className="animate-pulse space-y-3">
          <div className={`h-4 ${surfaces.tertiary} rounded w-1/3`} />
          <div className={`h-3 ${surfaces.tertiary} rounded w-2/3`} />
          <div className={`h-3 ${surfaces.tertiary} rounded w-1/2`} />
        </div>
      </div>
    ))}
    <span className="sr-only">Loading automations…</span>
  </div>
);

const AutomationsAdminPage = () => {
  const listQuery = useAdminAutomations();
  const { meta, isLoading: metaLoading, error: metaError } = useAdminAutomationConditions();
  const setStateMutation = useSetAdminAutomationState();
  const deleteMutation = useDeleteAdminAutomation();
  const runNowMutation = useRunAdminAutomationNow();
  const { toast, show: showToast, dismiss: dismissToast } = useToast();

  const automations = useMemo(() => (Array.isArray(listQuery.data?.results) ? listQuery.data.results : []), [listQuery.data]);
  const summary = useMemo(() => summarizeAutomations(automations), [automations]);

  // Filters (client-side: the list is small and the summary strip needs the full set anyway).
  const [stateFilter, setStateFilter] = useState("");
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return automations.filter((a) => {
      if (stateFilter && a.state !== stateFilter) return false;
      if (!q) return true;
      return String(a.name || "").toLowerCase().includes(q) || String(a.slug || "").toLowerCase().includes(q);
    });
  }, [automations, stateFilter, search]);

  // Editor / drawer / dialogs
  const [editor, setEditor] = useState({ open: false, mode: "create", automation: null });
  const [runsFor, setRunsFor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [runNowTarget, setRunNowTarget] = useState(null);
  const [activateTarget, setActivateTarget] = useState(null); // { automation, preview: {status, count, error} }
  const activatePreviewRequest = useRef(0);

  const openCreate = () => setEditor({ open: true, mode: "create", automation: null });
  const openEdit = (automation) => setEditor({ open: true, mode: "edit", automation });
  const openDuplicate = (automation) => setEditor({ open: true, mode: "duplicate", automation });
  const closeEditor = () => setEditor((prev) => ({ ...prev, open: false }));

  const handleSaved = (saved, mode) => {
    closeEditor();
    showToast("success", mode === "edit" ? "Automation saved" : `Automation saved as draft${saved?.name ? ` — "${saved.name}"` : ""}`);
  };

  const busyId = setStateMutation.isPending ? setStateMutation.variables?.id : null;

  const setState = async (automation, state, successMessage) => {
    try {
      await setStateMutation.mutateAsync({ id: automation.id, state });
      showToast("success", successMessage);
    } catch (err) {
      showToast("error", err?.message || "Failed to change state.");
    }
  };

  const requestActivate = (automation) => {
    const requestId = activatePreviewRequest.current + 1;
    activatePreviewRequest.current = requestId;
    setActivateTarget({ automation, preview: { status: "loading", count: null, error: "" } });
    adminAutomationPreview(automationToPreviewPayload(automation))
      .then((data) => {
        if (activatePreviewRequest.current !== requestId) return;
        setActivateTarget((prev) =>
          prev && prev.automation.id === automation.id
            ? {
                ...prev,
                preview: {
                  status: "success",
                  count: Number(data?.would_enqueue_count ?? 0),
                  matched: Number(data?.situation_matched_count ?? 0),
                  eventLabel: data?.trigger_event_label || "",
                  recent: data?.recent_occurrences ?? null,
                  recentDays: data?.recent_occurrences_days ?? 30,
                  error: "",
                },
              }
            : prev
        );
      })
      .catch((err) => {
        if (activatePreviewRequest.current !== requestId) return;
        setActivateTarget((prev) =>
          prev && prev.automation.id === automation.id
            ? { ...prev, preview: { status: "error", count: null, error: err?.message || "Unknown error" } }
            : prev
        );
      });
  };

  const activateMessage = useMemo(() => {
    if (!activateTarget) return "";
    const { automation, preview } = activateTarget;
    if (automation.trigger_type === "event") {
      const label = preview.eventLabel || automation.trigger_event_label || automation.trigger_event || "its event";
      const volume =
        preview.status === "success" && preview.recent !== null && preview.recent !== undefined
          ? ` It happened about ${formatCount(preview.recent)} time${Number(preview.recent) === 1 ? "" : "s"} in the last ${preview.recentDays || 30} days.`
          : "";
      return `This will start firing every time "${label}" happens to a user, immediately — guards and the delivery policy still apply.${volume}`;
    }
    const schedule = `${formatRunDays(automation.run_days_of_week)} at ${String(automation.run_hour_utc).padStart(2, "0")}:00 UTC`;
    const tail = ` After that, each user gets the messages once — when they enter the situation. Next scheduled run: ${schedule}.`;
    if (preview.status === "loading") return `Calculating who is in this situation right now…${tail}`;
    if (preview.status === "error") return `Could not compute the preview (${preview.error}). You can still activate.${tail}`;
    const n = preview.count ?? 0;
    return `This will start enqueuing messages for ${formatCount(n)} user${n === 1 ? "" : "s"} on the next scheduled run (${formatCount(
      preview.matched ?? 0
    )} currently match the situation).${tail}`;
  }, [activateTarget]);

  const handleRunNow = async () => {
    const target = runNowTarget;
    if (!target) return;
    try {
      const res = await runNowMutation.mutateAsync(target.id);
      const run = res?.run;
      const dry = run?.dry_run;
      if (run?.status === "failed") {
        showToast("error", `Run failed: ${run.error || "unknown error"}`);
      } else {
        showToast(
          "success",
          `${dry ? "Dry run" : "Run"} completed — ${formatCount(run?.situation_matched_count ?? 0)} matched, ${formatCount(
            run?.enqueued_count ?? 0
          )} ${dry ? "would be enqueued" : "enqueued"}.`
        );
      }
    } catch (err) {
      showToast("error", err?.message || "Failed to run automation.");
    }
  };

  const handleDelete = async () => {
    const target = deleteTarget;
    if (!target) return;
    try {
      await deleteMutation.mutateAsync(target.id);
      showToast("success", `Deleted "${target.name}"`);
    } catch (err) {
      showToast("error", err?.message || "Failed to delete automation.");
    }
  };

  const situationLabel = (automation) => {
    const condition = findCondition(meta.situations, automation.situation_key);
    return {
      label: condition?.label || automation.situation_key || "—",
      params: formatParamsSummary(condition, automation.situation_params),
    };
  };

  const guardLabels = (automation) =>
    (automation.guards || []).map((g) => {
      const condition = findCondition(meta.guards, g.key);
      const params = formatParamsSummary(condition, g.params);
      return `${condition?.label || g.key}${params ? ` (${params})` : ""}`;
    });

  const isEmpty = !listQuery.isLoading && !listQuery.error && automations.length === 0;
  const noMatches = !listQuery.isLoading && !listQuery.error && automations.length > 0 && filtered.length === 0;

  const renderRowActions = (automation, { compact = false } = {}) => (
    <div className={`flex items-center ${compact ? "flex-wrap" : "justify-end"} gap-2`}>
      <GhostButton size="sm" onClick={() => openEdit(automation)} leftIcon={<PencilIcon size="sm" />}>
        Edit
      </GhostButton>
      <StateToggleButton
        automation={automation}
        busy={busyId === automation.id}
        onActivate={() => requestActivate(automation)}
        onPause={() => setState(automation, "paused", `Paused "${automation.name}"`)}
      />
      <RowActionsMenu
        automation={automation}
        onRunNow={() => setRunNowTarget(automation)}
        onViewRuns={() => setRunsFor(automation)}
        onDuplicate={() => openDuplicate(automation)}
        onDelete={() => setDeleteTarget(automation)}
        onSetDraft={() => setState(automation, "draft", `"${automation.name}" moved back to draft`)}
      />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <Toast toast={toast} onDismiss={dismissToast} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className={`${typography.h2} ${textColors.primary}`}>Automations</h1>
          <p className={`mt-1 ${typography.bodySmall} ${textColors.secondary} max-w-3xl`}>
            Condition-driven messages. A routine automation scans on a schedule and fires once per user when they enter a
            situation; an event automation fires the moment something happens to a user (a website created, a subscription
            cancelled, …). Both check optional guards, then enqueue inbox / email messages.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 shrink-0">
          <SecondaryButton onClick={() => listQuery.refetch()} disabled={listQuery.isFetching} className="w-full sm:w-auto whitespace-nowrap">
            {listQuery.isFetching ? "Refreshing…" : "Refresh"}
          </SecondaryButton>
          <PrimaryButton
            onClick={openCreate}
            leftIcon={<PlusIcon size="sm" />}
            className="w-full sm:w-auto whitespace-nowrap"
            disabled={!!metaError}
            title={metaError ? "The editor needs the condition metadata to load first" : undefined}
          >
            New automation
          </PrimaryButton>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        <SummaryTile label="Automations" value={summary.total} hint="All automations" />
        <SummaryTile label="Active" value={summary.active} tone="success" hint="Running on schedule" />
        <SummaryTile label="Paused" value={summary.paused} tone="warning" hint="Skipped by the scheduler" />
        <SummaryTile label="Drafts" value={summary.draft} hint="Never send — preview only" />
        <SummaryTile label="Scheduled" value={summary.scheduled} hint="Messages waiting for delivery across all automations" />
        <SummaryTile label="Sent" value={summary.sent} hint="Messages delivered across all automations" />
      </div>

      {/* Errors */}
      {listQuery.error ? (
        <div className="rounded-2xl px-4 py-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800" role="alert">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className={`${typography.bodySmall} text-red-800 dark:text-red-300`}>
              Failed to load automations: {listQuery.error?.message || "Unknown error"}
            </p>
            <SecondaryButton size="sm" onClick={() => listQuery.refetch()} disabled={listQuery.isFetching}>
              Retry
            </SecondaryButton>
          </div>
        </div>
      ) : null}
      {metaError ? (
        <div className="rounded-2xl px-4 py-3 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800" role="alert">
          <p className={`${typography.bodySmall} text-yellow-800 dark:text-yellow-300`}>
            Condition metadata could not be loaded ({metaError?.message || "Unknown error"}) — situation and guard names fall back
            to their keys and the editor cannot open until it loads.
          </p>
        </div>
      ) : null}

      {/* List */}
      <Card variant="glass">
        <CardHeader
          title="All automations"
          subtitle={
            listQuery.isLoading
              ? "Loading…"
              : `${formatCount(filtered.length)} of ${formatCount(automations.length)} shown`
          }
          actions={
            <div className="hidden md:block w-72">
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or slug…"
                aria-label="Search automations"
                size="sm"
              />
            </div>
          }
        />
        <CardBody className="mt-4 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by state">
              {STATE_FILTERS.map((f) => {
                const active = stateFilter === f.value;
                const count = f.value ? summary[f.value] : summary.total;
                return (
                  <button
                    key={f.value || "all"}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setStateFilter(f.value)}
                    className={`${typography.button} px-3 py-1.5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 ${
                      active ? "bg-blue-600 dark:bg-blue-500 text-white" : `${surfaces.tertiary} ${textColors.primary} ${surfaces.hover}`
                    }`}
                  >
                    {f.label}
                    <span className={`ml-1.5 tabular-nums ${active ? "text-white/80" : textColors.tertiary}`}>{formatCount(count)}</span>
                  </button>
                );
              })}
            </div>
            <div className="md:hidden">
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or slug…"
                aria-label="Search automations"
                size="sm"
              />
            </div>
          </div>

          {listQuery.isLoading ? (
            <SkeletonRows />
          ) : listQuery.error ? null : isEmpty ? (
            <div className={`rounded-2xl ${surfaces.secondary}`}>
              <EmptyState
                icon={<ClockIcon size="xl" className={textColors.muted} />}
                title="No automations yet"
                description="Create your first automation: pick a trigger (a routine schedule or an event), choose who it applies to, add optional guards and write the messages. It starts as a draft, so nothing is sent until you activate it."
                action={
                  <PrimaryButton onClick={openCreate} leftIcon={<PlusIcon size="sm" />}>
                    New automation
                  </PrimaryButton>
                }
              />
            </div>
          ) : noMatches ? (
            <div className={`rounded-2xl ${surfaces.secondary}`}>
              <EmptyState
                title="No automations match"
                description="Try another search term or clear the state filter."
                action={
                  <SecondaryButton
                    onClick={() => {
                      setSearch("");
                      setStateFilter("");
                    }}
                  >
                    Clear filters
                  </SecondaryButton>
                }
              />
            </div>
          ) : (
            <>
              {/* Mobile + tablet: cards */}
              <ol className="space-y-3 lg:hidden" aria-label="Automations">
                {filtered.map((automation) => {
                  const situation = situationLabel(automation);
                  const guards = guardLabels(automation);
                  return (
                    <li key={automation.id} className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-4`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className={`${typography.body} ${textColors.primary} font-medium break-words`}>{automation.name}</div>
                          <div className={`${typography.caption} ${textColors.muted} font-mono break-all`}>{automation.slug}</div>
                        </div>
                        <AutomationStateBadge state={automation.state} />
                      </div>
                      {automation.description ? (
                        <p className={`mt-2 ${typography.bodySmall} ${textColors.secondary} line-clamp-2`}>{automation.description}</p>
                      ) : null}
                      <dl className={`mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t ${borders.glass}`}>
                        <div>
                          <dt className={`${typography.caption} ${textColors.muted}`}>Trigger</dt>
                          <dd className={`${typography.bodySmall} ${textColors.primary}`}>{formatTrigger(automation, meta)}</dd>
                        </div>
                        <div>
                          <dt className={`${typography.caption} ${textColors.muted}`}>Situation</dt>
                          <dd className={`${typography.bodySmall} ${textColors.primary}`}>
                            {situation.label}
                            {situation.params ? <span className={`block ${typography.caption} ${textColors.tertiary}`}>{situation.params}</span> : null}
                          </dd>
                        </div>
                        <div>
                          <dt className={`${typography.caption} ${textColors.muted}`}>Guards</dt>
                          <dd className={`${typography.bodySmall} ${textColors.primary}`}>
                            {guards.length === 0 ? "None" : guards.map((g) => <span key={g} className="block">{g}</span>)}
                          </dd>
                        </div>
                        <div>
                          <dt className={`${typography.caption} ${textColors.muted}`}>Actions</dt>
                          <dd className={`${typography.bodySmall} ${textColors.primary}`}>
                            {(automation.actions || []).length} · <ChannelChips actions={automation.actions} />
                            <span className={`block ${typography.caption} ${textColors.tertiary}`}>
                              {(automation.actions || []).map((a) => formatDelay(a.delay_minutes)).join(", ")}
                            </span>
                          </dd>
                        </div>
                        <div>
                          <dt className={`${typography.caption} ${textColors.muted}`}>Priority</dt>
                          <dd className={`${typography.bodySmall} ${textColors.primary} tabular-nums`}>{automation.priority}</dd>
                        </div>
                        <div>
                          <dt className={`${typography.caption} ${textColors.muted}`}>Last run</dt>
                          <dd>
                            <LastRunCell automation={automation} />
                          </dd>
                        </div>
                        <div>
                          <dt className={`${typography.caption} ${textColors.muted}`}>Messages</dt>
                          <dd>
                            <MessagesCell counts={automation.message_counts} automation={automation} />
                          </dd>
                        </div>
                      </dl>
                      <div className={`mt-3 pt-3 border-t ${borders.glass}`}>{renderRowActions(automation, { compact: true })}</div>
                    </li>
                  );
                })}
              </ol>

              {/* Desktop: table */}
              <div className={`hidden lg:block rounded-2xl border ${borders.glass} overflow-hidden`}>
                <div className={`overflow-x-auto ${surfaces.glassCard}`}>
                  <table className="min-w-full text-left" aria-label="Automations">
                    <thead className={surfaces.glass}>
                      <tr className={`${typography.caption} ${textColors.secondary}`}>
                        <th className="px-3 py-2 font-medium">Name</th>
                        <th className="px-3 py-2 font-medium">State</th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">Trigger</th>
                        <th className="px-3 py-2 font-medium">Situation</th>
                        <th className="px-3 py-2 font-medium">Guards</th>
                        <th className="px-3 py-2 font-medium">Actions</th>
                        <th className="px-3 py-2 font-medium text-right" title="Higher wins when the global frequency cap binds">
                          Priority
                        </th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap" title="matched → entered → guards passed → enqueued">
                          Last run
                        </th>
                        <th className="px-3 py-2 font-medium">Messages</th>
                        <th className="px-3 py-2 font-medium text-right">
                          <span className="sr-only">Row actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((automation) => {
                        const situation = situationLabel(automation);
                        const guards = guardLabels(automation);
                        return (
                          <tr key={automation.id} className={`border-t ${borders.glass} align-top ${surfaces.hover}`}>
                            <td className="px-3 py-3 min-w-[200px] max-w-[280px]">
                              <div className={`${typography.bodySmall} ${textColors.primary} font-medium break-words`}>{automation.name}</div>
                              <div className={`${typography.caption} ${textColors.muted} font-mono break-all`}>{automation.slug}</div>
                              {automation.description ? (
                                <div className={`mt-0.5 ${typography.caption} ${textColors.tertiary} line-clamp-2`} title={automation.description}>
                                  {automation.description}
                                </div>
                              ) : null}
                            </td>
                            <td className="px-3 py-3">
                              <AutomationStateBadge state={automation.state} />
                            </td>
                            <td className={`px-3 py-3 ${typography.bodySmall} ${textColors.primary} whitespace-nowrap`}>{formatTrigger(automation, meta)}</td>
                            <td className="px-3 py-3 min-w-[160px] max-w-[240px]">
                              <div className={`${typography.bodySmall} ${textColors.primary}`}>{situation.label}</div>
                              {situation.params ? (
                                <div className={`${typography.caption} ${textColors.tertiary} break-words`}>{situation.params}</div>
                              ) : null}
                            </td>
                            <td className="px-3 py-3">
                              {guards.length === 0 ? (
                                <span className={`${typography.caption} ${textColors.muted}`}>None</span>
                              ) : (
                                <span
                                  className={`${chipClass} cursor-help`}
                                  title={guards.join("\n")}
                                  aria-label={`${guards.length} guard${guards.length === 1 ? "" : "s"}: ${guards.join("; ")}`}
                                >
                                  {guards.length} guard{guards.length === 1 ? "" : "s"}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-3 min-w-[150px]">
                              <div className={`${typography.bodySmall} ${textColors.primary}`}>
                                {(automation.actions || []).length} action{(automation.actions || []).length === 1 ? "" : "s"}
                              </div>
                              <div className="mt-1">
                                <ChannelChips actions={automation.actions} />
                              </div>
                              <div className={`mt-0.5 ${typography.caption} ${textColors.tertiary}`}>
                                {(automation.actions || []).map((a) => formatDelay(a.delay_minutes)).join(", ")}
                              </div>
                            </td>
                            <td className={`px-3 py-3 ${typography.bodySmall} ${textColors.primary} text-right tabular-nums`}>{automation.priority}</td>
                            <td className="px-3 py-3 min-w-[170px]">
                              <LastRunCell automation={automation} />
                            </td>
                            <td className="px-3 py-3">
                              <MessagesCell counts={automation.message_counts} automation={automation} />
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap">{renderRowActions(automation)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Run history — across every automation */}
      <AutomationRunHistorySection automationOptions={meta?.automation_slugs || []} />

      {/* Global delivery policy */}
      <AutomationSettingsCard />

      {/* Editor */}
      <AutomationEditor
        isOpen={editor.open && !metaLoading && !metaError}
        mode={editor.mode}
        automation={editor.automation}
        meta={meta}
        onClose={closeEditor}
        onSaved={handleSaved}
      />

      {/* Runs drawer */}
      <AutomationRunsDrawer isOpen={!!runsFor} automation={runsFor} onClose={() => setRunsFor(null)} />

      {/* Confirmations */}
      <ConfirmDialog
        isOpen={!!activateTarget}
        onClose={() => {
          if (setStateMutation.isPending) return;
          setActivateTarget(null);
        }}
        onConfirm={async () => {
          const target = activateTarget?.automation;
          if (!target) return;
          await setState(target, "active", `Activated "${target.name}" — it runs ${formatRunDays(target.run_days_of_week)} at ${String(target.run_hour_utc).padStart(2, "0")}:00 UTC`);
        }}
        variant="warning"
        title={activateTarget?.automation?.state === "paused" ? `Resume "${activateTarget?.automation?.name}"?` : `Activate "${activateTarget?.automation?.name}"?`}
        message={activateMessage}
        confirmText={activateTarget?.automation?.state === "paused" ? "Resume" : "Activate"}
        cancelText="Cancel"
        isLoading={setStateMutation.isPending}
        size="md"
      />

      <ConfirmDialog
        isOpen={!!runNowTarget}
        onClose={() => {
          if (runNowMutation.isPending) return;
          setRunNowTarget(null);
        }}
        onConfirm={handleRunNow}
        variant={runNowTarget?.state === "draft" ? "info" : "warning"}
        title={runNowTarget?.state === "draft" ? "Run a dry run now?" : "Run now?"}
        message={
          runNowTarget?.state === "draft"
            ? `"${runNowTarget?.name}" is a draft, so this records a dry run: the full funnel is evaluated and saved in the run history, but no messages are enqueued.`
            : `This evaluates "${runNowTarget?.name}" right now, outside its schedule, and enqueues messages for every user who enters the situation and passes the guards. This is a real run, not a dry run.`
        }
        confirmText={runNowTarget?.state === "draft" ? "Run dry run" : "Run now"}
        cancelText="Cancel"
        isLoading={runNowMutation.isPending}
        size="md"
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => {
          if (deleteMutation.isPending) return;
          setDeleteTarget(null);
        }}
        onConfirm={handleDelete}
        variant="danger"
        title={`Delete "${deleteTarget?.name}"?`}
        message="The automation, its schedule and its memberships are removed. Messages already sent or scheduled stay in the message history. This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deleteMutation.isPending}
        size="md"
      />
    </div>
  );
};

export default AutomationsAdminPage;
